import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { hasSupabaseConfig } from "@/lib/supabase";
import { siteConfig, type SiteConfig } from "@/lib/site-config";
import { siteSettingsService, type SiteSettingsFeaturesOverride, type SiteSettingsRow } from "@/services/siteSettingsService";
import { STALE_TIMES, queryKeys } from "@/lib/query-config";

type EffectiveFeatures = typeof siteConfig.features;
type EffectiveNavigation = typeof siteConfig.navigation;
const SITE_SETTINGS_CACHE_KEY = "site_settings_cache_v1";

const getCachedSiteSettings = (): SiteSettingsRow | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.localStorage.getItem(SITE_SETTINGS_CACHE_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as SiteSettingsRow;
    if (!parsed || typeof parsed !== "object") return undefined;

    return parsed;
  } catch {
    return undefined;
  }
};

const setCachedSiteSettings = (settings: SiteSettingsRow | null): void => {
  if (typeof window === "undefined") return;

  try {
    if (!settings) {
      window.localStorage.removeItem(SITE_SETTINGS_CACHE_KEY);
      return;
    }

    window.localStorage.setItem(SITE_SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore cache write errors (private mode/storage limits)
  }
};

const mergeFeatures = (overrides?: SiteSettingsFeaturesOverride | null): EffectiveFeatures => {
  if (!overrides) return siteConfig.features;

  return {
    ...siteConfig.features,
    donation: {
      ...siteConfig.features.donation,
      ...(overrides.donation ?? {}),
    },
    membership: {
      ...siteConfig.features.membership,
      ...(overrides.membership ?? {}),
    },
    events: {
      ...siteConfig.features.events,
      ...(overrides.events ?? {}),
    },
    gallery: {
      ...siteConfig.features.gallery,
      ...(overrides.gallery ?? {}),
    },
    news: {
      ...siteConfig.features.news,
      ...(overrides.news ?? {}),
    },
    programs: {
      ...siteConfig.features.programs,
      ...(overrides.programs ?? {}),
    },
    dailyFacts: {
      ...siteConfig.features.dailyFacts,
      ...(overrides.dailyFacts ?? {}),
    },
  };
};

const mergeNavigation = (overrides?: SiteSettingsFeaturesOverride | null): EffectiveNavigation => {
  if (!overrides?.navigation) return siteConfig.navigation;

  return {
    ...siteConfig.navigation,
    ...overrides.navigation,
  };
};

export const useSiteSettings = () => {
  const initialData = getCachedSiteSettings();

  const query = useQuery({
    queryKey: queryKeys.siteSettings,
    queryFn: async () => {
      const result = await siteSettingsService.getSiteSettings();
      if (result.success) {
        setCachedSiteSettings(result.data ?? null);
        return result.data;
      }
      // Return null on error, will use defaults
      return null;
    },
    initialData,
    enabled: hasSupabaseConfig,
    staleTime: STALE_TIMES.siteSettings,
  });

  const effectiveFeatures = useMemo(() => {
    return mergeFeatures(query.data?.features ?? null);
  }, [query.data?.features]);

  const effectiveNavigation = useMemo(() => {
    return mergeNavigation(query.data?.features ?? null);
  }, [query.data?.features]);

  // Merge organization details from database with defaults
  const organizationDetails = useMemo(() => {
    const dbDetails = query.data?.organization_details;
    if (!dbDetails) return null;
    
    // Return the database details (they should be complete)
    return dbDetails;
  }, [query.data?.organization_details]);

  return {
    ...query,
    features: effectiveFeatures,
    navigation: effectiveNavigation,
    organizationDetails,
  };
};


